<template>
  <div id="linkedin_activity_graph">
    <ContainerInfoItem
      source="linkedin_activity_graph"
      :title="title"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <LineChart
        v-else-if="chartCategories && chartData"
        id="linkedin_activity"
        primary-color="#0A66C2"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="linkedin_activity"
        text="No LinkedIn activity data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch, watchEffect} from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { formatDateToMonthDay } from "@/src/utils/dateTimeUtils.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import LineChart from "@/src/components/charts/LineChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  title: { type: String, default: "LinkedIn activity" },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const seriesColors = {
  "Connection Requests": "#0A66C2",
  "InMail Sent": "#0073B1",
  "InMail Replied": "#2867B2",
  "Messages Sent": "#00A0DC",
  "Messages Replied": "#0077B5",
  Skipped: "#AC1C1E",
};

const processLinkedinActivityData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }

  const sortedData = [...data].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  const categories = sortedData.map((item) => formatDateToMonthDay(item.date));

  const seriesData = [
    {
      name: "Connection Requests",
      data: sortedData.map((item) => item.connection_sent),
      color: seriesColors["Connection Requests"],
    },
    {
      name: "InMail Sent",
      data: sortedData.map((item) => item.inmail_sent),
      color: seriesColors["InMail Sent"],
    },
    {
      name: "InMail Replied",
      data: sortedData.map((item) => item.inmail_replied),
      color: seriesColors["InMail Replied"],
    },
    {
      name: "Messages Sent",
      data: sortedData.map((item) => item.message_sent),
      color: seriesColors["Messages Sent"],
    },
    {
      name: "Messages Replied",
      data: sortedData.map((item) => item.message_replied),
      color: seriesColors["Messages Replied"],
    },
    // {
    //   name: "Skipped",
    //   data: sortedData.map((item) => item.skipped),
    //   color: seriesColors["Skipped"],
    // },
  ];

  return { categories, seriesData };
};

const fetchLinkedinActivity = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      sequence_id: filters.value?.sequence?.id,
    };

    const response =
      await AnalyticService.V2.fetchSequenceLinkedinEngagement(params);

    if (response?.success && response?.data) {
      const data = response?.data;

      const { categories, seriesData } = processLinkedinActivityData(data);

      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching LinkedIn activity: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch([range, filters, userIds, teamIds], () => {
  if ((range.value && userIds.value && teamIds?.value) || (filters.value?.sequence?.id)) {
    fetchLinkedinActivity();
  }
}, { deep: true });

onMounted(() => {
  // fetchLinkedinActivity();
});

useAnalyticsRefresh(fetchLinkedinActivity, [range, userIds, teamIds]);
</script>
